import { AppThunkAction } from '../reducers';

// Actions
import * as Actions from '../actions';

// Services
import * as service from '../../services/ProtocolConsumptionSyncProcess.Service';

// Models
import { ProtocolConsumptionSyncProcess } from '../../models/Entities/Protocols/ProtocolConsumptionSyncProcess';
import { ProtocolConsumptionSyncProcessLog } from '../../models/Entities/Protocols/ProtocolConsumptionSyncProcessLog';
import { ErrorType } from '../../models/HttpError';
import { IPagedResult } from '../../models/HttpPagedResult';
import { ISyncProcessLogsFilters } from '../../models/Filters/SyncProcessLogsFilters';
import { SyncProcessLogSeverityEnum } from '../../models/Enums/SyncProcessLogSeverityEnum';
import { DEFAULT_MIN_LOG_SEVERITY } from '../../services/ProtocolConsumptionSyncProcess.Service';

/**
 * Tamaño de página con el que se abre el log. El endpoint admite hasta 500,
 * pero cada evento se dibuja como una fila expandible con varios componentes
 * de Fluent: la primera pintada es lo que más se siente al abrir el panel.
 * Con 50 entran unas cuatro pantallas y el resto queda a un clic; quien
 * necesite más lo sube desde el selector de filas.
 */
export const SYNC_PROCESS_LOGS_DEFAULT_PAGE_SIZE = 50;

type KnownAction =
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetAllByProtocolIdRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetAllByProtocolIdSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetAllByProtocolIdFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetByIdRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetByIdSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetByIdFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLogsByIdRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLogsByIdSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLogsByIdFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLastByProtocolIdRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLastByProtocolIdSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessGetLastByProtocolIdFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessCreateRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessCreateSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessCreateFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessUpdateRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessUpdateSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessUpdateFailureAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessDeleteRequestAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessDeleteSuccessAction
    | Actions.ProtocolConsumptionSyncProcess.ProtocolConsumptionSyncProcessDeleteFailureAction;

export const ActionCreators = {
    GetAllProtocolConsumptionSyncProcessByProtocolId:
        (protocolId: number): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETALL_BYPROTOCOLID_FAILURE',
                        protocolId: protocolId,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                if (appState) {
                    service.GetProtocolConsumptionSyncProcess(protocolId, token)
                        .then((list: ProtocolConsumptionSyncProcess[]) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETALL_BYPROTOCOLID_SUCCESS',
                                protocolId: protocolId,
                                processes: list
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETALL_BYPROTOCOLID_FAILURE',
                                protocolId: protocolId,
                                error: error
                            });
                        });
                }
            },

    GetProtocolConsumptionSyncProcessById:
        (protocolId: number, syncProcessId: string): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GET_BYID_FAILURE',
                        protocolId: protocolId,
                        syncProcessId: syncProcessId,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                if (appState) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GET_BYID_REQUEST',
                        protocolId: protocolId,
                        syncProcessId: syncProcessId,
                    });

                    service.GetProtocolConsumptionSyncProcessById(token, syncProcessId)
                        .then((process: ProtocolConsumptionSyncProcess) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GET_BYID_SUCCESS',
                                process: process
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GET_BYID_FAILURE',
                                protocolId: protocolId,
                                syncProcessId: syncProcessId,
                                error: error
                            });
                        }
                        );
                }
            },
    GetProtocolConsumptionSyncProcessLogsById:
        (
            protocolId: number,
            syncProcessId: string,
            pageNumber: number = 1,
            pageSize: number = SYNC_PROCESS_LOGS_DEFAULT_PAGE_SIZE,
            filters?: ISyncProcessLogsFilters
        ): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLOGS_BYID_FAILURE',
                        protocolId: protocolId,
                        syncProcessId: syncProcessId,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                dispatch({
                    type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLOGS_BYID_REQUEST',
                    protocolId: protocolId,
                    syncProcessId: syncProcessId,
                });

                if (appState) {
                    service.GetProtocolConsumptionSyncProcessLogsById(token, syncProcessId, pageNumber, pageSize, filters)
                        .then((result: IPagedResult<ProtocolConsumptionSyncProcessLog[]>) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLOGS_BYID_SUCCESS',
                                protocolId: protocolId,
                                syncProcessId: syncProcessId,
                                logs: result?.data ?? [],
                                paginationState: {
                                    // Se toma lo que informa el servidor y, si no
                                    // viene, lo que se pidió: la pantalla necesita
                                    // sí o sí un estado de paginado coherente.
                                    pageNumber: result?.pageNumber ?? pageNumber,
                                    pageSize: result?.pageSize ?? pageSize,
                                    totalPages: result?.totalPages ?? 0,
                                    totalRecords: result?.totalRecords ?? 0,
                                    firstPage: result?.firstPage,
                                    lastPage: result?.lastPage,
                                    nextPage: result?.nextPage,
                                    previousPage: result?.previousPage,
                                },
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLOGS_BYID_FAILURE',
                                protocolId: protocolId,
                                syncProcessId: syncProcessId,
                                error: error,
                            });
                        });
                }
            },
    GetLastProtocolConsumptionSyncProcessByProtocolId:
        (protocolId: number): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLAST_BYPROTOCOLID_FAILURE',
                        protocolId: protocolId,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                dispatch({
                    type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLAST_BYPROTOCOLID_REQUEST',
                    protocolId: protocolId,
                });

                if (appState) {
                    service.GetLastProtocolConsumptionSyncProcess(protocolId, token)
                        .then((process: ProtocolConsumptionSyncProcess) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLAST_BYPROTOCOLID_SUCCESS',
                                protocolId: protocolId,
                                process: process
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_GETLAST_BYPROTOCOLID_FAILURE',
                                protocolId: protocolId,
                                error: error
                            });
                        });
                }
            },
    CreateProtocolConsumptionSyncProcess:
        (
            protocolId: number,
            useMedicalPayableDefinitions: boolean,
            minLogSeverity: SyncProcessLogSeverityEnum = DEFAULT_MIN_LOG_SEVERITY
        ): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_CREATE_FAILURE',
                        protocolId: protocolId,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                if (appState) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_CREATE_REQUEST',
                        protocolId: protocolId
                    });

                    service.AddProtocolConsumptionSyncProcess(protocolId, useMedicalPayableDefinitions, token, minLogSeverity)
                        .then((process: ProtocolConsumptionSyncProcess) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_CREATE_SUCCESS',
                                process: process
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_CREATE_FAILURE',
                                protocolId: protocolId,
                                error: error
                            });
                        });
                }
            },
    UpdateProtocolConsumptionSyncProcess:
        (process: ProtocolConsumptionSyncProcess): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_UPDATE_FAILURE',
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                if (appState) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_UPDATE_REQUEST',
                        process: process
                    });

                    service.UpdateProtocolConsumptionSyncProcess(token, process)
                        .then((process: ProtocolConsumptionSyncProcess) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_UPDATE_SUCCESS',
                                process: process
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_UPDATE_FAILURE',
                                error: error
                            });
                        });
                }
            },
    DeleteProtocolConsumptionSyncProcess:
        (protocolId: number, id: string): AppThunkAction<KnownAction> =>
            (dispatch, getState) => {
                const appState = getState();
                var token: string = '';

                if (appState.oidc?.user?.access_token === undefined) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_DELETE_FAILURE',
                        protocolId: protocolId,
                        id: id,
                        error: {
                            ErrorCode: 401,
                            ErrorMessage: 'Not authorized',
                            Errors: []
                        }
                    });
                    return;
                } else {
                    token = appState.oidc.user.access_token;
                };

                if (appState) {
                    dispatch({
                        type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_DELETE_REQUEST',
                        protocolId: protocolId,
                        id: id
                    });

                    service.DeleteProtocolConsumptionSyncProcess(id, token)
                        .then(() => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_DELETE_SUCCESS',
                                protocolId: protocolId,
                                id: id
                            });
                        })
                        .catch((error: ErrorType) => {
                            dispatch({
                                type: 'PROTOCOLCONSUMPTIONSYNCPROCESS_DELETE_FAILURE',
                                protocolId: protocolId,
                                id: id,
                                error: error
                            });
                        });
                }
            }
};

